import { ApplicationInsights } from '@microsoft/applicationinsights-web'

const CONNECTION_STRING = process.env.VUE_APP_APPINSIGHTS_CONNECTION_STRING

let appInsights = null

// Matches backend photo-proxy paths once photo proxying ships (e.g. /api/places/.../photo
// or .../media). Only applied to <img> resources loaded from the API host, so ordinary
// API calls can't be misclassified.
const PROXY_PHOTO_PATH = /photo|media/i

/**
 * Classify an <img> resource URL as a tracked photo source.
 * @param {string} url - Absolute or relative resource URL
 * @returns {'google'|'proxy'|null} null means "not a photo we track"
 */
export function classifyPhotoSource(url) {
  try {
    const target = new URL(url, window.location.origin)
    if (
      target.hostname === 'googleusercontent.com' ||
      target.hostname.endsWith('.googleusercontent.com')
    ) {
      return 'google'
    }

    const apiBase = process.env.VUE_APP_API_BASE_URL
    if (apiBase) {
      const api = new URL(apiBase, window.location.origin)
      // Compare host (incl. port: dev serves SPA and API on different localhost
      // ports) and require the API base path, so SPA-served static images can
      // never be misclassified as proxy photos
      const basePath = api.pathname.replace(/\/$/, '')
      if (
        target.host === api.host &&
        target.pathname.startsWith(`${basePath}/`) &&
        PROXY_PHOTO_PATH.test(target.pathname)
      ) {
        return 'proxy'
      }
    }
  } catch {
    // Unparseable URL: not something we can attribute
  }
  return null
}

// Reports every photo <img> load as a photo_load_ms metric via resource timing.
// Centralised here (instead of per-component @load handlers) so it covers all
// current and future photo renders, including the upcoming backend proxy URLs.
// `buffered: true` captures photos that loaded before init ran.
function startPhotoLoadObserver() {
  if (typeof PerformanceObserver === 'undefined') return

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries()) {
      if (entry.initiatorType !== 'img') continue
      const source = classifyPhotoSource(entry.name)
      if (!source) continue

      // entry.duration is available even for cross-origin resources without a
      // Timing-Allow-Origin header; transferSize is 0 in that case (and also 0
      // for cache hits), so treat it as a hint, not a reliable byte count.
      appInsights.trackMetric(
        { name: 'photo_load_ms', average: entry.duration },
        {
          source,
          transferSize: String(entry.transferSize ?? 0)
        }
      )
    }
  })

  observer.observe({ type: 'resource', buffered: true })
}

/**
 * Azure Application Insights telemetry for real-user monitoring.
 * Collects page views (SPA route changes), browser timings, ajax/fetch
 * dependency durations, JS errors, and a custom photo_load_ms metric.
 * No-ops when VUE_APP_APPINSIGHTS_CONNECTION_STRING is not configured.
 */
export const telemetryService = {
  /**
   * Initialize the SDK. Call once at app startup, before mount.
   */
  init() {
    if (!CONNECTION_STRING || appInsights) return

    appInsights = new ApplicationInsights({
      config: {
        connectionString: CONNECTION_STRING,
        // Tracks SPA route changes as page views via history API hooks
        enableAutoRouteTracking: true,
        // Keep correlation headers off cross-origin requests: the API and
        // googleusercontent would need CORS changes to accept them
        enableCorsCorrelation: false
      }
    })

    appInsights.loadAppInsights()

    // No manual trackPageView() here: vue-router's first navigation performs a
    // history.replaceState, which enableAutoRouteTracking already reports as a
    // page view — a manual call would double-count every landing page.

    // Expired Google photo URLs are HEAD-probed by usePhotoValidation and
    // return 403 by design; don't let those land as failed dependencies
    appInsights.addTelemetryInitializer((item) => {
      if (
        item.baseType === 'RemoteDependencyData' &&
        /(^|\.)googleusercontent\.com/.test(item.baseData?.target || '')
      ) {
        return false
      }
    })

    startPhotoLoadObserver()
  },

  /**
   * Attribute subsequent telemetry to the signed-in user.
   * Call after fetching the user profile, alongside analyticsService.initUser.
   * @param {Object} profile - User profile from /Users/me
   */
  setAuthenticatedUser(profile) {
    if (!appInsights || !profile?.id) return
    // Validated chars only: App Insights rejects ids with ,;=| or whitespace
    const userId = String(profile.id).replace(/[,;=| ]/g, '_')
    appInsights.setAuthenticatedUserContext(userId)
  },

  /**
   * Stop attributing telemetry to the previous user on logout.
   */
  clearAuthenticatedUser() {
    if (!appInsights) return
    appInsights.clearAuthenticatedUserContext()
  }
}
